import { queryOptions, useQuery } from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type {
  Application,
  Dashboard,
  DashboardDataSource,
  Integration,
  Widget,
} from '@/api/types'

/**
 * What a dashboard is made of: the widgets, where each one's numbers come from,
 * and the numbers themselves.
 *
 * Three queries rather than one, because they change for different reasons. The
 * widgets change when somebody adds or removes one, the data sources when
 * somebody reconfigures one, and a *result* when the underlying table changes --
 * which nothing tells us about at all. Folding them together would mean
 * refetching the widget list to find out that a number moved.
 */

/**
 * The dashboard itself, for its name and description.
 *
 * Fetched by id rather than read out of the workspace's application list, which
 * already holds it. A link straight to `/dashboard/12` says nothing about which
 * workspace it is in, and the sidebar will have loaded whichever workspace was
 * last visited -- so the list that holds the answer is often not the list that
 * is loaded.
 */
export function dashboardQuery(dashboardId: number) {
  return queryOptions({
    queryKey: qk.dashboard(dashboardId),
    staleTime: Infinity,
    queryFn: async (): Promise<Dashboard | null> => {
      const application: Application = await unwrap(
        getApi().GET('/api/applications/{application_id}/', {
          params: { path: { application_id: dashboardId } },
        }),
      )
      // A url naming a database is not a url naming a dashboard, and answering
      // with one anyway would render an empty board rather than saying so.
      return application.type === 'dashboard' ? application : null
    },
  })
}

export function useDashboard(dashboardId: number) {
  return useQuery(dashboardQuery(dashboardId))
}

export function widgetsQuery(dashboardId: number) {
  return queryOptions({
    queryKey: qk.widgets(dashboardId),
    staleTime: Infinity,
    queryFn: (): Promise<Widget[]> =>
      unwrap(
        getApi().GET('/api/dashboard/{dashboard_id}/widgets/', {
          params: { path: { dashboard_id: dashboardId } },
        }),
      ),
  })
}

export function useWidgets(dashboardId: number) {
  return useQuery(widgetsQuery(dashboardId))
}

export function dataSourcesQuery(dashboardId: number) {
  return queryOptions({
    queryKey: qk.dataSources(dashboardId),
    staleTime: Infinity,
    queryFn: (): Promise<DashboardDataSource[]> =>
      unwrap(
        getApi().GET('/api/dashboard/{dashboard_id}/data-sources/', {
          params: { path: { dashboard_id: dashboardId } },
        }),
      ),
  })
}

export function useDataSources(dashboardId: number) {
  return useQuery(dataSourcesQuery(dashboardId))
}

/**
 * One data source's answer.
 *
 * A `POST` that reads: dispatching runs the aggregation and returns the result,
 * and there is no `GET` for it. It is still a query -- it is the server's answer
 * to a question the page asks, cached under the data source that asked it -- and
 * the method is an artefact of the endpoint taking a dispatch context body.
 *
 * The global error toast is off. A data source with no field chosen answers 400
 * every time it is asked, and the widget says so on its face; a toast would say
 * it again on every page load, for something the reader is already looking at.
 */
export function dataSourceResultQuery(dashboardId: number, dataSourceId: number) {
  return queryOptions({
    queryKey: qk.dataSourceResult(dashboardId, dataSourceId),
    staleTime: Infinity,
    meta: { skipGlobalErrorHandler: true },
    queryFn: (): Promise<unknown> =>
      unwrap(
        getApi().POST('/api/dashboard/data-sources/{data_source_id}/dispatch/', {
          params: { path: { data_source_id: dataSourceId } },
        }),
      ),
  })
}

export function useDataSourceResult(dashboardId: number, dataSourceId: number) {
  return useQuery(dataSourceResultQuery(dashboardId, dataSourceId))
}

/**
 * What this dashboard's data sources are allowed to read.
 *
 * Fetched once per dashboard and kept, because it is a catalogue rather than a
 * reading: it changes when somebody makes a table, not when somebody types in
 * one. The whole tree of databases, tables and views arrives on the
 * integration's `context_data`, so choosing a table and choosing a view of it
 * are the same request.
 *
 * Asked for only while a settings panel is open -- the board itself never needs
 * it, and it is the largest response on the page.
 */
export function integrationsQuery(dashboardId: number) {
  return queryOptions({
    queryKey: qk.integrations(dashboardId),
    staleTime: Infinity,
    queryFn: (): Promise<Integration[]> =>
      unwrap(
        getApi().GET('/api/application/{application_id}/integrations/', {
          params: { path: { application_id: dashboardId } },
        }),
      ),
  })
}

export function useIntegrations(dashboardId: number) {
  return useQuery(integrationsQuery(dashboardId))
}
